import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import _ from 'lodash';
import moment from 'moment';
import commaNumber from 'comma-number';
import './NotificationsSends.scss';

import { getNotificationSends } from '../../api/notifications';
import RangeSelect, { RANGE_LABELS } from './elements/RangeSelect';
import LabeledSelect from './elements/LabeledSelect';
import InfoTip from './elements/InfoTip';
import HeaderToolbar from './elements/HeaderToolbar';
import { UNCLASSIFIED, humanizeName, humanizeCategory } from '../../helpers/notification_helpers';

const ALL = 'all';
const withAll = options => [{ value: ALL, label: 'All' }, ...options];
const categoryKey = row => row.budgetCategory || UNCLASSIFIED;
// Shadow receipts sit beside the hold reasons in the same reason-and-count form; they are not holds, but they explain a quiet row.
const holdChips = row => [...row.topHoldReasons, ...(row.shadow > 0 ? [{ reason: 'shadow', count: row.shadow }] : [])];
const FAILURE_REASON_SHORT = {
  no_subscribed_devices: 'no device',
  no_recipient: 'no address',
  provider_rejected: 'refused',
  provider_error: 'errored',
  stale_reservation: 'unsettled',
  unknown: 'no reason'
};

const SORT_VALUES = {
  activity: r => (r.sent || 0) + (r.shadow || 0) + (r.held || 0),
  sent: r => r.sent || 0,
  held: r => r.held || 0,
  failed: r => r.failed || 0
};

const HEADER_TIPS = {
  notification: 'One row per notification type, named by its registry short code. Click a name to open the template.',
  category: 'The budget category, which is the unit push caps are counted in. Unclassified means the template has no budgetCategory yet.',
  mode:
    'Where the notification sits on the launch ladder. Live sends when its code runs. Shadow files a receipt for what would have sent and sends nothing.',
  sent: 'Receipts marked sent in this range: handed to the push or email provider.',
  held: 'Receipts a rule stopped in this range: the recipient\u2019s switch, a frequency cap, an age gate or the holdout group.',
  holdReason: 'The two most common reasons for the holds, with counts. Shadow receipts are listed separately because nothing was meant to send.',
  failed:
    'Receipts the provider did not deliver in this range, with the most common reason beneath: no subscribed device, refused, errored, or released by the reaper.',
  trend: 'Sent per time bucket across the range; the last bar is now.',
  flags: 'unclassified: no budget category yet. live, no receipts: the template exists but filed nothing in the range (not shown at 24 hours).'
};

const ATTENTION_FLAGS = ['unclassified', 'live, no receipts'];
const countOrDash = n => (n == null ? '—' : commaNumber(n));

// One row per notification type: what the ledger says it did over the range, joined to the rulebook and the registry.
// The aggregate is slow to build, so the page keeps what each range returned (`cache`, keyed by range)
// for as long as the page is open: switching to Templates or Recipients and back costs nothing, leaving
// the Notifications page drops it. The reload button refetches the current range on demand.
const NotificationsSends = ({ onSelectTemplate, templatesByName, toolbarSlot = null, cache = null, onCache = null }) => {
  const [range, setRange] = useState('7d');
  const [reloadCount, setReloadCount] = useState(0);
  const lastReloadCount = useRef(0);
  const [ownCache, setOwnCache] = useState({});
  const results = cache || ownCache;
  const store = onCache || ((key, result) => setOwnCache(existing => ({ ...existing, [key]: result })));
  const [isFetching, setIsFetching] = useState(!results['7d']);
  const [filter, setFilter] = useState('');
  const [category, setCategory] = useState(ALL);
  const [mode, setMode] = useState(ALL);
  const [flag, setFlag] = useState(ALL);
  const [sort, setSort] = useState({ key: 'activity', desc: true });
  const cached = results[range];
  const rows = cached?.rows || [];

  const hasCached = !!cached;
  useEffect(() => {
    const forceReload = reloadCount !== lastReloadCount.current;
    lastReloadCount.current = reloadCount;
    if (hasCached && !forceReload) {
      setIsFetching(false);
      return undefined;
    }
    let cancelled = false;
    setIsFetching(true);
    getNotificationSends(range)
      .then(resp => !cancelled && store(range, { rows: resp.rows || [], fetchedAt: new Date() }))
      .catch(() => !cancelled && window.ALERT?.error?.('Failed to load sends.'))
      .finally(() => !cancelled && setIsFetching(false));
    return () => (cancelled = true);
  }, [range, reloadCount]);

  const reload = () => setReloadCount(n => n + 1);

  const visibleRows = useMemo(() => {
    const q = filter.trim().toLowerCase();
    const matching = rows.filter(
      r =>
        (!q || [r.type, humanizeName(r.type), r.registry?.name || ''].some(text => text.toLowerCase().includes(q))) &&
        (category === ALL || categoryKey(r) === category) &&
        (mode === ALL || r.mode.mode === mode) &&
        (flag === ALL || r.flags.includes(flag))
    );
    return _.orderBy(matching, [SORT_VALUES[sort.key], 'type'], [sort.desc ? 'desc' : 'asc', 'asc']);
  }, [rows, filter, category, mode, flag, sort]);

  // First click sorts a column high to low; a second click flips it.
  const sortBy = key => setSort(current => ({ key, desc: current.key === key ? !current.desc : true }));
  const sortMark = key => (sort.key === key ? (sort.desc ? ' ▾' : ' ▴') : '');

  const withReceipts = visibleRows.filter(r => (r.sent || 0) + (r.held || 0) + (r.failed || 0) + (r.shadow || 0) > 0).length;

  // Filter options come from the rows themselves, so a value only appears once something carries it.
  const categoryOptions = withAll(
    _.sortBy(_.uniq(rows.map(categoryKey))).map(key => ({ value: key, label: humanizeCategory(key === UNCLASSIFIED ? null : key) }))
  );
  const modeOptions = withAll(_.sortBy(_.uniq(rows.map(r => r.mode.mode))).map(value => ({ value, label: value })));
  const flagOptions = withAll(_.sortBy(_.uniq(rows.flatMap(r => r.flags))).map(value => ({ value, label: value })));

  return (
    <div className='notifications-sends-outer'>
      <HeaderToolbar slot={toolbarSlot} className='notifications-sends-outer'>
        <div className='sends-controls'>
          <RangeSelect value={range} onChange={setRange} />
          <LabeledSelect label='Category' value={category} onChange={setCategory} options={categoryOptions} />
          <LabeledSelect label='Mode' value={mode} onChange={setMode} options={modeOptions} />
          <LabeledSelect label='Flags' value={flag} onChange={setFlag} options={flagOptions} />
          <span className='sends-summary'>
            {isFetching ? 'Loading…' : `${RANGE_LABELS[range]} · ${visibleRows.length} notification types, ${withReceipts} with receipts`}
          </span>
          <input className='sends-filter' type='text' placeholder='Search' value={filter} onChange={e => setFilter(e.target.value)} />
          <span className='sends-reload-wrap'>
            <button type='button' className={cn('sends-reload', { spinning: isFetching })} title='Reload' onClick={reload} disabled={isFetching}>
              ↻
            </button>
            <span className='sends-updated'>
              {isFetching ? 'Updating…' : cached?.fetchedAt ? `Updated at ${moment(cached.fetchedAt).format('h:mm a')}` : ''}
            </span>
          </span>
        </div>
      </HeaderToolbar>

      <div className='sends-table'>
        <div className='sends-row head'>
          <span>
            Notification
            <InfoTip message={HEADER_TIPS.notification} />
          </span>
          <span>
            Category
            <InfoTip message={HEADER_TIPS.category} />
          </span>
          <span>
            Mode
            <InfoTip message={HEADER_TIPS.mode} />
          </span>
          <span className={cn('num sortable', { sorted: sort.key === 'sent' })} onClick={() => sortBy('sent')}>
            Sent{sortMark('sent')}
            <InfoTip message={HEADER_TIPS.sent} />
          </span>
          <span className={cn('num sortable', { sorted: sort.key === 'held' })} onClick={() => sortBy('held')}>
            Held{sortMark('held')}
            <InfoTip message={HEADER_TIPS.held} />
          </span>
          <span>
            Top hold reason
            <InfoTip message={HEADER_TIPS.holdReason} />
          </span>
          <span className={cn('num sortable', { sorted: sort.key === 'failed' })} onClick={() => sortBy('failed')}>
            Failed{sortMark('failed')}
            <InfoTip message={HEADER_TIPS.failed} />
          </span>
          <span className='trend'>
            Trend
            <InfoTip message={HEADER_TIPS.trend} />
          </span>
          <span>
            Flags
            <InfoTip message={HEADER_TIPS.flags} />
          </span>
        </div>
        {visibleRows.map(row => {
          const template = templatesByName[row.type];
          const isRegistryOnly = row.mode.source === 'registry';
          return (
            <div key={row.type} className={cn('sends-row', { 'registry-only': isRegistryOnly })}>
              <span className={cn('send-name', { clickable: !!template })} onClick={() => template && onSelectTemplate?.(template)}>
                <span className='name'>{(isRegistryOnly && row.registry?.name) || humanizeName(row.type)}</span>
                <span className='code'>{row.type}</span>
              </span>
              <span className={cn('category', { unclassified: !row.budgetCategory && !isRegistryOnly })}>
                {isRegistryOnly ? '—' : humanizeCategory(row.budgetCategory)}
              </span>
              <span>
                <ModeBadge mode={row.mode} />
              </span>
              <span className='num'>{countOrDash(row.sent)}</span>
              <span className='num'>{countOrDash(row.held)}</span>
              <span className='reasons'>
                {holdChips(row).length === 0
                  ? '—'
                  : holdChips(row).map(({ reason, count }) => (
                      <span key={reason} className={cn('reason', { shadow: reason === 'shadow' })}>
                        <code>{reason}</code> {commaNumber(count)}
                      </span>
                    ))}
              </span>
              <span className='num failed-cell'>
                {countOrDash(row.failed)}
                {row.topFailureReason && (
                  <code
                    className='failure-reason'
                    title={`${commaNumber(row.topFailureReason.count)} of ${commaNumber(row.failed)} failed for this reason`}
                  >
                    {FAILURE_REASON_SHORT[row.topFailureReason.reason] || row.topFailureReason.reason}
                  </code>
                )}
              </span>
              <span className='trend'>{row.trend ? <Sparkline values={row.trend} /> : <span className='dash'>—</span>}</span>
              <span className='flags'>
                {row.flags.map(flag => (
                  <span key={flag} className={cn('flag', ATTENTION_FLAGS.includes(flag) ? 'attention' : 'neutral')}>
                    {flag}
                  </span>
                ))}
              </span>
            </div>
          );
        })}
        {!isFetching && visibleRows.length === 0 && <div className='sends-empty'>No notification types match.</div>}
      </div>
    </div>
  );
};

NotificationsSends.propTypes = {
  toolbarSlot: PropTypes.instanceOf(Element),
  cache: PropTypes.object,
  onCache: PropTypes.func,
  onSelectTemplate: PropTypes.func,
  templatesByName: PropTypes.object.isRequired
};

const MODE_TITLES = {
  worker: 'Current launch mode used by the notification-sends worker',
  send: 'Current launch mode, read on every send',
  registry: 'Status from the registry; no template in code, so the ledger never sees it'
};

const ModeBadge = ({ mode }) => (
  <span className={cn('mode-badge', mode.mode)} title={MODE_TITLES[mode.source]}>
    {mode.mode}
  </span>
);

ModeBadge.propTypes = {
  mode: PropTypes.shape({ mode: PropTypes.string.isRequired, source: PropTypes.string }).isRequired
};

// Sent per bucket, scaled to the row's own peak; the latest bucket is emerald.
const Sparkline = ({ values }) => {
  const max = Math.max(1, ...values);
  return (
    <span className='sparkline'>
      {values.map((v, i) => (
        <span
          key={i}
          className={cn('bar', { latest: i === values.length - 1 })}
          style={{ height: `${Math.max(2, Math.round((v / max) * 16))}px` }}
          title={commaNumber(v)}
        />
      ))}
    </span>
  );
};

Sparkline.propTypes = {
  values: PropTypes.arrayOf(PropTypes.number).isRequired
};

export default NotificationsSends;
